/**
 * File Parser Utilities Module
 * 
 * Comprehensive utility module for parsing Japanese salary and bonus data files.
 * This module handles various file formats, encodings, and data structures commonly
 * used in Japanese payroll systems.
 * 
 * Features:
 * - Automatic encoding detection (UTF-8, Shift_JIS)
 * - CSV parsing with Japanese text support
 * - Salary and bonus data extraction
 * - Metadata extraction from file headers
 * - Date parsing from Japanese date formats
 * - Robust error handling and validation
 * - Extended CJK character support (e.g., "濵" U+6F35)
 * - File encoding conversion to UTF-8
 * 
 * File Format Support:
 * - Salary data files (91+ columns)
 * - Bonus data files (41+ columns)
 * - CSV format with Japanese headers
 * - Metadata in first row
 * 
 * Used by:
 * - DataUpload component for file parsing
 * - ConstructionDataUpload component for construction worker data
 * - File validation utilities
 * 
 * @module FileParserUtils
 */

/**
 * Parsed Salary Data Interface
 * 
 * Comprehensive data structure for parsed salary information from CSV files.
 * This interface represents all the fields that can be extracted from salary data files,
 * including employee information, working hours, allowances, deductions, and payments.
 * 
 * The interface supports Japanese salary data formats with bilingual field names
 * for better understanding of the data structure.
 * 
 * @interface ParsedSalaryData
 */
export interface ParsedSalaryData {
  employeeCode: string;                    // 従業員コード - Employee identification code
  employeeName: string;                    // 氏名 - Employee full name
  department: string;                      // 所属 - Department/affiliation
  baseSalaryCategory: string;              // 基本給区分 - Base salary category
  employmentCategory: string;              // 就業区分 - Employment type
  employeeCategory: string;                // 従業員区分 - Employee category
  payrollLock: string;                     // 給与ロック - Payroll lock status
  workingDays: number;                     // 就業日数 - Total working days
  attendanceDays: number;                  // 出勤日数 - Actual attendance days
  absentDays: number;                      // 欠勤日数 - Absence days
  paidLeaveDays: number;                   // 有休日数 - Paid leave days used
  specialLeaveDays: number;                // 特休日数 - Special leave days
  holidayWorkDays: number;                 // 休出日数 - Holiday work days
  legalHolidayWorkDays: number;            // 法内休出日数 - Legal holiday work days
  compensatoryLeaveDays: number;           // 代休日数 - Compensatory leave days
  mealCount: number;                       // 食事回数 - Meal count
  nightDutyCount: number;                  // 宿直回数 - Night duty count
  lateEarlyCount: number;                  // 遅早回数 - Late/early count
  attendanceHours: string;                 // 出勤時間 - Attendance hours
  lateEarlyTime: string;                   // 遅早時間 - Late/early time
  regularOvertimeHours: string;            // 普通残業時間 - Regular overtime hours
  nightOvertimeHours: string;              // 深夜残業時間 - Night overtime hours
  holidayOvertimeHours: string;            // 休出残業時間 - Holiday overtime hours
  holidayNightTime: string;                // 休出深夜時間 - Holiday night time
  legalHolidayOvertimeHours: string;       // 法休残業時間 - Legal holiday overtime hours
  legalHolidayNightTime: string;           // 法休深夜時間 - Legal holiday night time
  legalOvertimeHours: string;              // 法内残業時間 - Legal overtime hours
  baseSalary: number;                      // 基本給 - Base salary amount
  positionAllowance: number;               // 役職手当 - Position allowance
  familyAllowance: number;                 // 家族手当 - Family allowance
  housingAllowance: number;                // 住宅手当 - Housing allowance
  qualificationAllowance: number;          // 資格手当 - Qualification allowance
  diligenceAllowance: number;              // 精勤手当 - Diligence allowance
  fieldAllowance: number;                  // 現場手当 - Field work allowance
  overtimeAllowance: number;               // 時間外手当 - Overtime allowance
  otherAllowance: number;                  // その他手当 - Other allowances
  yearEndAdjustment: number;               // 年末調整額 - Year-end adjustment
  longServiceAward: number;                // 永年勤続表彰 - Long service award
  taxableCommutingExpense: number;         // 課税通勤費 - Taxable commuting expense
  nonTaxableCommutingExpense: number;      // 非税通勤費 - Non-taxable commuting expense
  taxableMealCost: number;                 // 課税食事代 - Taxable meal cost
  nonTaxableMealCost: number;              // 非税食事代 - Non-taxable meal cost
  taxableNightDuty: number;                // 課税宿直 - Taxable night duty
  nonTaxableNightDuty: number;             // 非税宿直 - Non-taxable night duty
  taxableSalaryIncrease: number;           // 課税昇給額 - Taxable salary increase
  nonTaxableSalaryIncrease: number;        // 非税昇給額 - Non-taxable salary increase
  regularOvertime: number;                 // 普通残業 - Regular overtime amount
  nightOvertime: number;                   // 深夜残業 - Night overtime amount
  holidayOvertime: number;                 // 休出残業 - Holiday overtime amount
  holidayNight: number;                    // 休出深夜 - Holiday night amount
  legalHolidayOvertimeAmount: number;      // 法休残業 - Legal holiday overtime amount
  legalHolidayNightAmount: number;         // 法休深夜 - Legal holiday night amount
  legalOvertimeAmount: number;             // 法内残業 - Legal overtime amount
  absenceDeduction: number;                // 欠勤控除 - Absence deduction
  lateEarlyDeduction: number;              // 遅早控除 - Late/early deduction
  totalOtherAllowances: number;            // 他手当計 - Total other allowances
  totalPayment: number;                    // 支給合計 - Total payment
  healthInsurance: number;                 // 健康保険 - Health insurance
  longTermCareInsurance: number;           // 介護保険 - Long-term care insurance
  pensionInsurance: number;                // 厚生年金 - Pension insurance
  welfareFund: number;                     // 厚生基金 - Welfare fund
  employmentInsurance: number;             // 雇用保険 - Employment insurance
  incomeTax: number;                       // 所得税 - Income tax
  residenceTax: number;                    // 住民税 - Residence tax
  residenceTaxCorrection: number;          // 住民税訂正 - Residence tax correction
  employeeSavings: number;                 // 財形貯蓄 - Employee savings
  rent: number;                            // 家賃 - Rent
  totalDeductions: number;                 // 控除合計 - Total deductions
  previousFraction: number;                // 前回端数 - Previous fraction
  currentFraction: number;                 // 今回端数 - Current fraction
  yearEndAdjustment2: number;              // 年末調整額 - Year-end adjustment (2)
  deductedPayment: number;                 // 差引支給額 - Deducted payment amount
  actualPayment: number;                   // 実支給額 - Actual payment amount
  bankTransfer1: number;                   // 振込金額１ - Bank transfer amount 1
  bankTransfer2: number;                   // 振込金額２ - Bank transfer amount 2
  bankTransfer3: number;                   // 振込金額３ - Bank transfer amount 3
  cashPayment: number;                     // 現金支給額 - Cash payment amount
  inKindPayment: number;                   // 現物支給額 - In-kind payment amount
  numberOfDependentsEtc: number;           // 扶養等の数 - Number of dependents
  remainingPaidLeaveDays: number;          // 有休残日数 - Remaining paid leave days
  taxableAmount: number;                   // 課税対象額 - Taxable amount
  socialInsuranceTargetCash: number;       // 社保対象金銭 - Social insurance target cash
  socialInsuranceTargetInKind: number;     // 社保対象現物 - Social insurance target in-kind
  fixedWageTotal: number;                  // 固定賃金計 - Fixed wage total
  laborInsuranceTargetAmount: number;      // 労保対象額 - Labor insurance target amount
  employmentInsuranceTargetAmount: number; // 雇用対象額 - Employment insurance target amount
  taxablePaymentCumulative: number;        // 課税支給累計 - Cumulative taxable payment
  socialInsuranceDeductionCumulative: number; // 社保控除累計 - Cumulative social insurance deduction
  incomeTaxCumulative: number;             // 所得税累計 - Cumulative income tax
  
  // Personal Messages - Individual messages for each employee
  personalMessage1: string;                // 個人メッセージ1 - Personal message 1
  personalMessage2: string;                // 個人メッセージ2 - Personal message 2
}

/**
 * Parsed Bonus Data Interface
 * 
 * Comprehensive data structure for parsed bonus information from CSV files.
 * This interface represents all the fields that can be extracted from bonus data files,
 * including employee information, bonus amounts, deductions, and payments.
 * 
 * The interface supports Japanese bonus data formats with bilingual field names
 * for better understanding of the data structure.
 * 
 * @interface ParsedBonusData
 */
export interface ParsedBonusData {
  employeeCode: string;                    // 従業員コード
  employeeName: string;                    // 氏名
  department: string;                      // 所属
  employmentCategory: string;              // 就業区分
  employeeCategory: string;                // 従業員区分
  bonusLock: string;                       // 賞与ロック
  bonus: number;                           // 賞与
  performanceBonus: number;                // 業績賞与
  executiveBonus: number;                  // 役員賞与
  incentiveMoney: number;                  // 報奨金
  otherAllowancesTotal: number;            // 他手当計
  totalPayment: number;                    // 支給合計
  healthInsurance: number;                 // 健康保険
  longTermCareInsurance: number;           // 介護保険
  pensionInsurance: number;                // 厚生年金
  welfareFund: number;                     // 厚生基金
  employmentInsurance: number;             // 雇用保険
  incomeTax: number;                       // 所得税
  travelSavings: number;                   // 旅行積立
  employeeSavings: number;                 // 財形貯蓄
  unionDues: number;                       // 組合費
  otherDeductionsTotal: number;            // 他控除計
  totalDeductions: number;                 // 控除合計
  previousFraction: number;                // 前回端数
  currentFraction: number;                 // 今回端数
  yearEndAdjustment: number;               // 年末調整額
  netPaymentAmount: number;                // 差引支給額
  actualPaymentAmount: number;             // 実支給額
  transferAmount1: number;                 // 振込金額1
  transferAmount2: number;                 // 振込金額2
  transferAmount3: number;                 // 振込金額3
  cashPaymentAmount: number;               // 現金支給額
  inKindPaymentAmount: number;             // 現物支給額
  numberOfDependentsEtc: number;           // 扶養等の数
  taxableAmount: number;                   // 課税対象額
  laborInsuranceTargetAmount: number;      // 労保対象額
  taxablePaymentCumulative: number;        // 課税支給累計
  socialInsuranceDeductionCumulative: number; // 社保控除累計
  incomeTaxCumulative: number;             // 所得税累計
  bonusName?: string;                      // 賞与名称
  paymentDate?: string;                    // 支給日
  affiliation?: string;                    // 所属 全社
  
  // Personal Messages
  personalMessage1: string;                // 個人ﾒｯｾｰｼﾞ1
  personalMessage2: string;                // 個人ﾒｯｾｰｼﾞ2
}

/**
 * File Parse Result Interface
 * 
 * Result structure returned by file parsing operations.
 * Contains parsed data, metadata, validation errors, and file type information.
 * 
 * @interface FileParseResult
 * @property {boolean} success - Whether parsing was successful
 * @property {ParsedSalaryData[] | ParsedBonusData[]} data - Parsed data array
 * @property {string[]} errors - Array of parsing errors if any
 * @property {'salary' | 'bonus'} fileType - Type of file parsed
 * @property {string[]} headers - Column headers from the file
 * @property {Object} [extractedDate] - Extracted date information (year, month, bonusPeriod)
 * @property {Object} [metadata] - Additional metadata from file (payroll dates, periods, etc.)
 */
export interface FileParseResult {
  success: boolean;
  data: ParsedSalaryData[] | ParsedBonusData[];
  errors: string[];
  fileType: 'salary' | 'bonus';
  headers: string[];
  extractedDate?: {
    year: number;
    month: number;
    bonusPeriod?: number; // Store month number instead of season
  };
  metadata?: {
    payrollYearMonth?: string;    // 給与年月
    payrollDate?: string;         // 給与日
    paymentDate?: string;         // 支給日
    paymentSchedule?: string;     // 支給スケジュール
    totalRecords?: string;        // 件数 総数
    bonusYearMonth?: string;      // 賞与年月
    bonusPeriod?: string;         // 賞与期間
  };
}

/**
 * File Parser Utilities Class
 * 
 * Comprehensive utility class for parsing Japanese salary and bonus data files.
 * This class handles various file formats, encodings, and data structures commonly
 * used in Japanese payroll systems.
 * 
 * Features:
 * - Automatic encoding detection (UTF-8, Shift_JIS)
 * - CSV parsing with Japanese text support
 * - Salary and bonus data extraction
 * - Metadata extraction from file headers
 * - Date parsing from Japanese date formats
 * - Robust error handling and validation
 */
class FileParserUtils {
  
  /**
   * Detects the file encoding based on byte patterns
   * 
   * @param buffer - ArrayBuffer containing the file data
   * @returns string - Detected encoding ('UTF-8' or 'Shift_JIS')
   * 
   * This method analyzes byte patterns to determine the most likely encoding:
   * - Checks for UTF-8 BOM (Byte Order Mark)
   * - Analyzes Shift_JIS patterns common in Japanese files
   * - Defaults to Shift_JIS for Japanese content
   */
  detectEncoding(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    
    // Check for UTF-8 BOM first (strong indicator)
    if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
      return 'UTF-8';
    }
    
    // Prefer UTF-8 when the buffer decodes cleanly as UTF-8 (no replacement chars) and has Japanese.
    // This avoids mis-detecting UTF-8 files (e.g. 佐藤) as Shift_JIS because UTF-8 CJK bytes look like Shift_JIS patterns.
    try {
      const testUtf8Decode = new TextDecoder('utf-8', { fatal: false }).decode(buffer);
      const hasReplacement = testUtf8Decode.includes('\uFFFD');
      const hasJapanese = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(testUtf8Decode);
      if (!hasReplacement && hasJapanese) {
        return 'UTF-8';
      }
    } catch (e) {
      // ignore
    }
    
    // Try to detect if file contains extended CJK characters that require UTF-8
    // Sample first 10KB to check for extended characters
    const sampleSize = Math.min(bytes.length, 10240);
    try {
      const sampleBuffer = buffer.slice(0, sampleSize);
      const testUtf8 = new TextDecoder('utf-8', { fatal: false }).decode(sampleBuffer);
      
      // Check for extended CJK characters (beyond U+9FAF) that aren't in Shift_JIS
      // Character "濵" (U+6F35) is one such character
      if (testUtf8.includes('濵')) {
        // If we see this character correctly decoded as UTF-8, use UTF-8
        return 'UTF-8';
      }
      
      // Also check for corruption patterns that suggest wrong encoding
      // If UTF-8 decode shows "濵" correctly but other decodings show "M口", prefer UTF-8
      const testShiftJIS = new TextDecoder('shift-jis', { fatal: false }).decode(sampleBuffer);
      if (testShiftJIS.includes('M口') && testUtf8.includes('濵')) {
        return 'UTF-8';
      }
      
      // Check for any extended CJK characters (CJK Extension A, B, etc.)
      // Characters in range U+3400-U+4DBF (Extension A) or beyond U+9FAF
      const extendedCJKPattern = /[\u3400-\u4DBF\u9FA6-\u9FFF]/;
      if (extendedCJKPattern.test(testUtf8)) {
        // If we detect extended CJK, prefer UTF-8
        return 'UTF-8';
      }
    } catch (e) {
      // If detection fails, continue with normal detection
    }
    
    // Check for Shift_JIS patterns (common in Japanese files)
    let shiftJISCount = 0;
    for (let i = 0; i < Math.min(bytes.length - 1, 1000); i++) {
      const byte1 = bytes[i];
      const byte2 = bytes[i + 1];
      
      // Shift_JIS patterns for Japanese characters
      if ((byte1 >= 0x81 && byte1 <= 0x9F) || (byte1 >= 0xE0 && byte1 <= 0xEF)) {
        if (byte2 >= 0x40 && byte2 <= 0xFC && byte2 !== 0x7F) {
          shiftJISCount++;
        }
      }
    }
    
    // If we find many Shift_JIS patterns, use Shift_JIS
    // But double-check for corruption patterns first
    if (shiftJISCount > 10) {
      try {
        const testContent = new TextDecoder('shift-jis', { fatal: false }).decode(buffer.slice(0, Math.min(1000, buffer.byteLength)));
        // If we see single ASCII letters followed by Japanese chars, might be corruption
        // This suggests UTF-8 content was mis-decoded as Shift_JIS
        if (testContent.match(/[A-Z][\u4E00-\u9FAF]/)) {
          return 'UTF-8'; // Prefer UTF-8 to avoid corruption
        }
      } catch (e) {
        // Ignore errors
      }
      return 'Shift_JIS';
    }
    
    // Default to UTF-8 for better compatibility with extended characters
    // This ensures characters like "濵" are properly handled
    return 'UTF-8';
  }

  // Convert buffer to string with proper encoding
  async bufferToString(buffer: ArrayBuffer, encoding: string): Promise<string> {
    try {
      if (encoding === 'UTF-8') {
        const result = new TextDecoder('utf-8').decode(buffer);
        return result;
      } else if (encoding === 'Shift_JIS') {
        // For Shift_JIS, try different decoders in order of preference
        const decoders = ['shift-jis', 'windows-31j', 'euc-jp', 'iso-2022-jp'];
        
        for (const decoder of decoders) {
          try {
            const result = new TextDecoder(decoder).decode(buffer);
            
            // Check if we can see Japanese characters properly
            if (result.includes('個人') || result.includes('メッセージ') || result.includes('給与')) {
              return result;
            } else if (result.includes('lү')) {
              continue; // Try next decoder
            } else {
              return result;
            }
          } catch (e) {
            continue;
          }
        }
        
        // If all Shift_JIS decoders fail, try UTF-8 as fallback
        try {
          const result = new TextDecoder('utf-8').decode(buffer);
          return result;
        } catch (e) {
          // Continue to final fallback
        }
      }
      
      // Final fallback to UTF-8 with non-fatal errors
      const result = new TextDecoder('utf-8', { fatal: false }).decode(buffer);
      return result;
      
    } catch (error) {
      // Emergency fallback
      const result = new TextDecoder('utf-8', { fatal: false }).decode(buffer);
      return result;
    }
  }

  // Parse CSV content with improved delimiter detection
  parseCSV(content: string): string[][] {
    // Split content into lines
    const lines = content.split(/\r?\n/);
    
    // Parse each line into columns
    const result: string[][] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line || line.trim() === '') continue;
      
      const row = this.parseCsvLine(line);
      if (row.length > 0) {
        result.push(row);
      }
    }
    
    return result;
  }
  
  private parseCsvLine(line: string): string[] {
    const row: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        row.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    row.push(current.trim());
    
    // Remove trailing empty columns
    while (row.length > 0 && row[row.length - 1] === '') {
      row.pop();
    }
    
    return row;
  }

  // Detect file type based on headers (improved)
  detectFileType(headers: string[]): 'salary' | 'bonus' {
    const headerText = headers.join(' ').toLowerCase();
    
    // Check for bonus-specific keywords
    if (headerText.includes('賞与') || headerText.includes('bonus') || 
        headerText.includes('賞与区分') || headerText.includes('基本賞与')) {
      return 'bonus';
    }
    
    // Default to salary
    return 'salary';
  }

  // Parse salary data with improved column mapping
  parseSalaryData(csvData: string[][]): ParsedSalaryData[] {
    const result: ParsedSalaryData[] = [];
    
    // For this custom format, line 0 is metadata, line 1 has the actual headers
    // Skip metadata row and use line 1 as headers
    const dataStartIndex = Math.min(3, csvData.length - 1); // Start from line 3 (after metadata and headers)
    
    // Get headers from line 1 (the actual data headers), not line 0 (metadata)
    const headers = csvData[1] || [];

    // Find personal message columns
    const personalMessageColumns = this.findPersonalMessageColumns(headers);
    
    for (let i = dataStartIndex; i < csvData.length; i++) {
      const row = csvData[i];
      if (row.length < 91) {
        continue; // Skip invalid rows
      }
      
      try {
        const data: ParsedSalaryData = {
          employeeCode: row[0] || '',
          employeeName: row[1] || '',
          department: row[2] || '',
          baseSalaryCategory: row[3] || '',
          employmentCategory: this.findColumnValue(row, headers, 'employmentCategory', '就業区分', row[4]) || '',
          employeeCategory: this.findColumnValue(row, headers, 'employeeCategory', '従業員区分', row[5]) || '',
          payrollLock: this.findColumnValue(row, headers, 'payrollLock', '給与ロック', row[6]) || '',
          workingDays: this.parseNumber(row[7]),
          attendanceDays: this.parseNumber(row[8]),
          absentDays: this.parseNumber(row[9]),
          paidLeaveDays: this.parseNumber(row[10]),
          specialLeaveDays: this.parseNumber(row[11]),
          holidayWorkDays: this.parseNumber(row[12]),
          legalHolidayWorkDays: this.parseNumber(row[13]),
          compensatoryLeaveDays: this.parseNumber(row[14]),
          mealCount: this.parseNumber(row[15]),
          nightDutyCount: this.parseNumber(row[16]),
          lateEarlyCount: this.parseNumber(row[17]),
          attendanceHours: row[18] || '',
          lateEarlyTime: row[19] || '',
          regularOvertimeHours: row[20] || '',
          nightOvertimeHours: row[21] || '',
          holidayOvertimeHours: row[22] || '',
          holidayNightTime: row[23] || '',
          legalHolidayOvertimeHours: row[24] || '',
          legalHolidayNightTime: row[25] || '',
          legalOvertimeHours: row[26] || '',
          baseSalary: this.parseNumber(row[27]),
          positionAllowance: this.parseNumber(row[28]),
          familyAllowance: this.parseNumber(row[29]),
          housingAllowance: this.parseNumber(row[30]),
          qualificationAllowance: this.parseNumber(row[31]),
          diligenceAllowance: this.parseNumber(row[32]),
          fieldAllowance: this.parseNumber(row[33]),
          overtimeAllowance: this.parseNumber(row[34]),
          otherAllowance: this.parseNumber(row[35]),
          yearEndAdjustment: this.parseNumber(row[36]),
          longServiceAward: this.parseNumber(row[37]),
          taxableCommutingExpense: this.parseNumber(row[38]),
          nonTaxableCommutingExpense: this.parseNumber(row[39]),
          taxableMealCost: this.parseNumber(row[40]),
          nonTaxableMealCost: this.parseNumber(row[41]),
          taxableNightDuty: this.parseNumber(row[42]),
          nonTaxableNightDuty: this.parseNumber(row[43]),
          taxableSalaryIncrease: this.parseNumber(row[44]),
          nonTaxableSalaryIncrease: this.parseNumber(row[45]),
          regularOvertime: this.parseNumber(row[46]),
          nightOvertime: this.parseNumber(row[47]),
          holidayOvertime: this.parseNumber(row[48]),
          holidayNight: this.parseNumber(row[49]),
          legalHolidayOvertimeAmount: this.parseNumber(row[50]),
          legalHolidayNightAmount: this.parseNumber(row[51]),
          legalOvertimeAmount: this.parseNumber(row[52]),
          absenceDeduction: this.parseNumber(row[53]),
          lateEarlyDeduction: this.parseNumber(row[54]),
          totalOtherAllowances: this.parseNumber(row[55]),
          totalPayment: this.parseNumber(row[56]),
          healthInsurance: this.parseNumber(row[57]),
          longTermCareInsurance: this.parseNumber(row[58]),
          pensionInsurance: this.parseNumber(row[59]),
          welfareFund: this.parseNumber(row[60]),
          employmentInsurance: this.parseNumber(row[61]),
          incomeTax: this.parseNumber(row[62]),
          residenceTax: this.parseNumber(row[63]),
          residenceTaxCorrection: this.parseNumber(row[64]),
          employeeSavings: this.parseNumber(row[65]),
          rent: this.parseNumber(row[66]),
          totalDeductions: this.parseNumber(row[67]),
          previousFraction: this.parseNumber(row[68]),
          currentFraction: this.parseNumber(row[69]),
          yearEndAdjustment2: this.parseNumber(row[70]),
          deductedPayment: this.parseNumber(row[71]),
          actualPayment: this.parseNumber(row[72]),
          bankTransfer1: this.parseNumber(row[73]),
          bankTransfer2: this.parseNumber(row[74]),
          bankTransfer3: this.parseNumber(row[75]),
          cashPayment: this.parseNumber(row[76]),
          inKindPayment: this.parseNumber(row[77]),
          numberOfDependentsEtc: this.parseNumber(row[78]),
          remainingPaidLeaveDays: this.parseNumber(row[79]),
          taxableAmount: this.parseNumber(row[80]),
          socialInsuranceTargetCash: this.parseNumber(row[81]),
          socialInsuranceTargetInKind: this.parseNumber(row[82]),
          fixedWageTotal: this.parseNumber(row[83]),
          laborInsuranceTargetAmount: this.parseNumber(row[84]),
          employmentInsuranceTargetAmount: this.parseNumber(row[85]),
          taxablePaymentCumulative: this.parseNumber(row[86]),
          socialInsuranceDeductionCumulative: this.parseNumber(row[87]),
          incomeTaxCumulative: this.parseNumber(row[88]),
          
          // Personal Messages - use found columns by name
          personalMessage1: this.findPersonalMessageColumnValue(row, headers, 1),
          personalMessage2: this.findPersonalMessageColumnValue(row, headers, 2)
        };
        
        // Validate required fields
        if (data.employeeCode && data.employeeName) {
          result.push(data);
        }
      } catch (error) {
        // Skip invalid rows
      }
    }
    
    return result;
  }

  // Find personal message columns by name
  private findPersonalMessageColumns(headers: string[]): { message1: number; message2: number } {
    let message1Index = -1;
    let message2Index = -1;
    
    // Try various possible column names for personal messages
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i] || '';
      const lowerHeader = header.toLowerCase();
      
      // Look for personal message 1
      if (message1Index === -1 && (
        lowerHeader.includes('個人メッセージ1') ||
        lowerHeader.includes('個人ﾒｯｾｰｼﾞ1') ||
        lowerHeader.includes('personal_message_1') ||
        lowerHeader.includes('personal_message1') ||
        lowerHeader.includes('message1') ||
        lowerHeader.includes('メッセージ1') ||
        lowerHeader.includes('ﾒｯｾｰｼﾞ1')
      )) {
        message1Index = i;
      }
      
      // Look for personal message 2
      if (message2Index === -1 && (
        lowerHeader.includes('個人メッセージ2') ||
        lowerHeader.includes('個人ﾒｯｾｰｼﾞ2') ||
        lowerHeader.includes('personal_message_2') ||
        lowerHeader.includes('personal_message2') ||
        lowerHeader.includes('message2') ||
        lowerHeader.includes('メッセージ2') ||
        lowerHeader.includes('ﾒｯｾｰｼﾞ2')
      )) {
        message2Index = i;
      }
    }
    
    // If not found by name, try to find by position (often last columns)
    if (message1Index === -1 && headers.length > 0) {
      message1Index = headers.length - 2; // Second to last column
    }
    if (message2Index === -1 && headers.length > 0) {
      message2Index = headers.length - 1; // Last column
    }
    
    return { message1: message1Index, message2: message2Index };
  }

  // Parse bonus data with improved column mapping for 41 columns (actual sample file structure)
  parseBonusData(csvData: string[][], metadata?: any): ParsedBonusData[] {
    const result: ParsedBonusData[] = [];
    
    // For this custom format, line 0 is metadata, line 1 has the actual headers
    // Skip metadata row and use line 1 as headers
    const dataStartIndex = Math.min(3, csvData.length - 1); // Start from line 3 (after metadata and headers)
    
    // Get headers from line 1 (the actual data headers), not line 0 (metadata)
    const headers = csvData[1] || [];

    
    for (let i = dataStartIndex; i < csvData.length; i++) {
      const row = csvData[i];
      if (row.length < 41) {
        continue; // Skip invalid rows
      }
      
      try {
        const data: ParsedBonusData = {
          employeeCode: row[0] || '',
          employeeName: row[1] || '',
          department: row[2] || '',
          employmentCategory: this.findColumnValue(row, headers, 'employmentCategory', '就業区分', row[3]) || '',
          employeeCategory: this.findColumnValue(row, headers, 'employeeCategory', '従業員区分', row[4]) || '',
          bonusLock: this.findColumnValue(row, headers, 'bonusLock', '賞与ロック', row[5]) || '',
          bonus: this.parseNumber(row[6]),
          performanceBonus: this.parseNumber(row[7]),
          executiveBonus: this.parseNumber(row[8]),
          incentiveMoney: this.parseNumber(row[9]),
          otherAllowancesTotal: this.parseNumber(row[10]),
          totalPayment: this.parseNumber(row[11]),
          healthInsurance: this.parseNumber(row[12]),
          longTermCareInsurance: this.parseNumber(row[13]),
          pensionInsurance: this.parseNumber(row[14]),
          welfareFund: this.parseNumber(row[15]),
          employmentInsurance: this.parseNumber(row[16]),
          incomeTax: this.parseNumber(row[17]),
          travelSavings: this.parseNumber(row[18]),
          employeeSavings: this.parseNumber(row[19]),
          unionDues: this.parseNumber(row[20]),
          otherDeductionsTotal: this.parseNumber(row[21]),
          totalDeductions: this.parseNumber(row[22]),
          previousFraction: this.parseNumber(row[23]),
          currentFraction: this.parseNumber(row[24]),
          yearEndAdjustment: this.parseNumber(row[25]),
          netPaymentAmount: this.parseNumber(row[26]),
          actualPaymentAmount: this.parseNumber(row[27]),
          transferAmount1: this.parseNumber(row[28]),
          transferAmount2: this.parseNumber(row[29]),
          transferAmount3: this.parseNumber(row[30]),
          cashPaymentAmount: this.parseNumber(row[31]),
          inKindPaymentAmount: this.parseNumber(row[32]),
          numberOfDependentsEtc: this.parseNumber(row[33]),
          taxableAmount: this.parseNumber(row[34]),
          laborInsuranceTargetAmount: this.parseNumber(row[35]),
          taxablePaymentCumulative: this.parseNumber(row[36]),
          socialInsuranceDeductionCumulative: this.parseNumber(row[37]),
          incomeTaxCumulative: this.parseNumber(row[38]),

          // Personal Messages - find columns by name
          personalMessage1: this.findPersonalMessageColumnValue(row, headers, 1),
          personalMessage2: this.findPersonalMessageColumnValue(row, headers, 2),

          // Use extracted metadata or default values
          bonusName: metadata?.bonusName || '',
          paymentDate: metadata?.paymentDate || '',
          affiliation: metadata?.affiliation || ''
        };
        
        // Debug logging for missing fields (sample first employee)
        if (result.length === 0 && data.employeeCode) {
          console.log('Sample bonus data parsing:', {
            employeeCode: data.employeeCode,
            employmentCategory: data.employmentCategory,
            employeeCategory: data.employeeCategory,
            bonusLock: data.bonusLock,
            bonusName: data.bonusName,
            paymentDate: data.paymentDate,
            affiliation: data.affiliation,
            metadata: metadata
          });
        }
        
        // Validate required fields
        if (data.employeeCode && data.employeeName) {
          result.push(data);
        }
      } catch (error) {
        // Skip invalid rows
      }
    }
    
    return result;
  }

  // Find column value by name or use fallback
  findColumnValue(row: string[], headers: string[], fieldName: string, japaneseName: string, fallbackValue: string): string {
    try {
      // First try to find by Japanese name
      const japaneseIndex = headers.findIndex(header => 
        header.toLowerCase().includes(japaneseName.toLowerCase()) ||
        header.toLowerCase().includes(fieldName.toLowerCase())
      );
      
      if (japaneseIndex >= 0 && japaneseIndex < row.length && row[japaneseIndex]) {
        return row[japaneseIndex].trim();
      }
      
      // Fallback to provided value
      return fallbackValue || '';
    } catch (error) {
      return fallbackValue || '';
    }
  }

  // Find personal message column value by index
  private findPersonalMessageColumnValue(row: string[], headers: string[], index: number): string {
    const lowerHeaders = headers.map(h => h.toLowerCase());
    const lowerRow = row.map(r => r.toLowerCase());

    for (let i = 0; i < lowerHeaders.length; i++) {
      const lowerHeader = lowerHeaders[i];
      if (lowerHeader.includes(`個人メッセージ${index}`) || lowerHeader.includes(`個人ﾒｯｾｰｼﾞ${index}`) ||
          lowerHeader.includes(`personal_message_${index}`) || lowerHeader.includes(`personal_message${index}`) ||
          lowerHeader.includes(`message${index}`)) {
        return row[i] || '';
      }
    }
    return ''; // Default to empty if not found
  }

  // Parse number safely with improved handling
  private parseNumber(value: string): number {
    if (!value || value.trim() === '') return 0;
    
    // Remove quotes, commas, and spaces
    const cleanValue = value.replace(/['"]/g, '').replace(/,/g, '').replace(/\s/g, '');
    
    // Handle time format (HH:MM)
    if (cleanValue.includes(':')) {
      const parts = cleanValue.split(':');
      if (parts.length === 2) {
        const hours = parseFloat(parts[0]) || 0;
        const minutes = parseFloat(parts[1]) || 0;
        return hours + (minutes / 60);
      }
    }
    
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  
  // Extract metadata from file content
  extractMetadataFromContent(content: string, fileType: 'salary' | 'bonus'): {
    payrollYearMonth?: string;
    payrollDate?: string;
    paymentDate?: string;
    paymentSchedule?: string;
    totalRecords?: string;
    bonusYearMonth?: string;
    bonusPeriod?: string;
    bonusName?: string;
    affiliation?: string;
  } {
    const metadata: any = {};
    
    try {
      const lines = content.split('\n');
      if (lines.length === 0) return metadata;

      const firstLine = lines[0];
      
      // Extract metadata fields from the first line
      const fields = firstLine.split(',').map(field => field.replace(/"/g, '').trim());
      
      if (fileType === 'salary') {
        if (fields.length >= 1) metadata.payrollYearMonth = fields[0];
        if (fields.length >= 2) metadata.payrollDate = fields[1];
        if (fields.length >= 3) metadata.paymentDate = fields[2];
        if (fields.length >= 4) metadata.paymentSchedule = fields[3];
        if (fields.length >= 5) metadata.totalRecords = fields[4];
      } else {
        if (fields.length >= 1) metadata.bonusYearMonth = fields[0];
        if (fields.length >= 2) metadata.bonusPeriod = fields[1];
        if (fields.length >= 3) metadata.paymentDate = fields[2];
        if (fields.length >= 4) metadata.totalRecords = fields[3];
        
        // Try to extract additional metadata from the content
        this.extractAdditionalMetadata(content, metadata);
      }
      
      return metadata;
    } catch (error) {
      return metadata;
    }
  }

  // Extract additional metadata from file content
  extractAdditionalMetadata(content: string, metadata: any): void {
    try {
      const lines = content.split('\n');
      
      // Look for bonus name patterns
      const bonusNamePatterns = [
        /賞与名称[:\s]*([^\n\r,]+)/i,
        /bonus[_\s]*name[:\s]*([^\n\r,]+)/i,
        /名称[:\s]*([^\n\r,]+)/i
      ];
      
      for (const pattern of bonusNamePatterns) {
        const match = content.match(pattern);
        if (match && match[1]) {
          metadata.bonusName = match[1].trim();
          break;
        }
      }
      
      // Look for affiliation patterns
      const affiliationPatterns = [
        /所属[:\s]*([^\n\r,]+)/i,
        /affiliation[:\s]*([^\n\r,]+)/i,
        /全社[:\s]*([^\n\r,]+)/i
      ];
      
      for (const pattern of affiliationPatterns) {
        const match = content.match(pattern);
        if (match && match[1]) {
          metadata.affiliation = match[1].trim();
          break;
        }
      }
      
      // Look for payment date patterns (more comprehensive)
      const paymentDatePatterns = [
        /支給日[:\s]*([^\n\r,]+)/i,
        /payment[_\s]*date[:\s]*([^\n\r,]+)/i,
        /支払日[:\s]*([^\n\r,]+)/i,
        /令和\s*(\d+)年\s*(\d+)月\s*(\d+)日/i
      ];
      
      for (const pattern of paymentDatePatterns) {
        const match = content.match(pattern);
        if (match && match[1]) {
          if (pattern.source.includes('令和')) {
            // Handle Japanese date format
            const year = parseInt(match[1]) + 2018;
            const month = match[2].padStart(2, '0');
            const day = match[3].padStart(2, '0');
            metadata.paymentDate = `${year}-${month}-${day}`;
          } else {
            metadata.paymentDate = match[1].trim();
          }
          break;
        }
      }
      
    } catch (error) {
      console.error('Error extracting additional metadata:', error);
    }
  }

  // Extract year and month from file content
  extractDateFromContent(content: string, fileType: 'salary' | 'bonus'): { year: number; month: number; bonusPeriod?: number } | null {
    try {
      const lines = content.split('\n');
      if (lines.length === 0) return null;

      const firstLine = lines[0];

      if (fileType === 'salary') {
        // Look for patterns like "給与年月 令和 6年 8月給与"
        const salaryPattern = /給与年月\s*令和\s*(\d+)年\s*(\d+)月/;
        const match = firstLine.match(salaryPattern);
        
        if (match) {
          const year = parseInt(match[1]) + 2018; // Convert Reiwa to Gregorian
          const month = parseInt(match[2]);
          return { year, month };
        }

        // Alternative pattern: "給与日 令和 6年 7月31日"
        const alternativePattern = /給与日\s*令和\s*(\d+)年\s*(\d+)月/;
        const altMatch = firstLine.match(alternativePattern);
        
        if (altMatch) {
          const year = parseInt(altMatch[1]) + 2018;
          const month = parseInt(altMatch[2]);
          return { year, month };
        }

        // Additional pattern: "支給日 令和 6年 8月23日"
        const paymentPattern = /支給日\s*令和\s*(\d+)年\s*(\d+)月/;
        const paymentMatch = firstLine.match(paymentPattern);
        
        if (paymentMatch) {
          const year = parseInt(paymentMatch[1]) + 2018;
          const month = parseInt(paymentMatch[2]);
          return { year, month };
        }
      } else {
        // Bonus data - look for patterns like "賞与年月 令和 6年1月"
        const bonusPattern = /賞与年月\s*令和\s*(\d+)年\s*(\d+)月/;
        const match = firstLine.match(bonusPattern);
        
        if (match) {
          const year = parseInt(match[1]) + 2018;
          const month = parseInt(match[2]);
          
          // Store the actual month number as bonus period
          return { year, month, bonusPeriod: month };
        }

        // Alternative pattern for bonus: "賞与期間 R6年7月賞与"
        const alternativePattern = /賞与期間\s*R(\d+)年(\d+)月/;
        const altMatch = firstLine.match(alternativePattern);
        
        if (altMatch) {
          const year = parseInt(altMatch[1]) + 2018;
          const month = parseInt(altMatch[2]);
          
          // Store the actual month number as bonus period
          return { year, month, bonusPeriod: month };
        }

        // Additional pattern: "支給日 令和 6年 8月 9日"
        const paymentPattern = /支給日\s*令和\s*(\d+)年\s*(\d+)月/;
        const paymentMatch = firstLine.match(paymentPattern);
        
        if (paymentMatch) {
          const year = parseInt(paymentMatch[1]) + 2018;
          const month = parseInt(paymentMatch[2]);
          
          // Store the actual month number as bonus period
          return { year, month, bonusPeriod: month };
        }

        // Look for specific bonus period patterns in the file content
        const specificBonusPatterns = [
          { pattern: /(\d+)月賞与/, period: 'monthly' },
          { pattern: /夏賞与/, period: 'summer' },
          { pattern: /冬賞与/, period: 'winter' },
          { pattern: /春賞与/, period: 'spring' },
          { pattern: /秋賞与/, period: 'autumn' },
          { pattern: /年賞与/, period: 'annual' }
        ];

        for (const { pattern, period } of specificBonusPatterns) {
          const periodMatch = content.match(pattern);
          if (periodMatch) {
            // Try to extract year from the same line or nearby lines
            const yearPattern = /令和\s*(\d+)年|R(\d+)年/;
            const yearMatch = content.match(yearPattern);
            
            if (yearMatch) {
              const year = parseInt(yearMatch[1] || yearMatch[2]) + 2018;
              let month = 1; // Default month
              
              // If it's a monthly pattern like "7月賞与", extract the month
              if (period === 'monthly' && periodMatch[1]) {
                month = parseInt(periodMatch[1]);
              }
              
              // Store the actual month number as bonus period
              return { year, month, bonusPeriod: month };
            }
          }
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Main file parsing method that orchestrates the entire parsing process
   * 
   * @param file - File object to parse (CSV format expected)
   * @returns Promise<FileParseResult> - Parsed data with metadata and validation results
   * 
   * This method performs the complete file parsing workflow:
   * 1. Reads file as ArrayBuffer for encoding detection
   * 2. Detects file encoding (UTF-8 or Shift_JIS)
   * 3. Converts buffer to string with proper encoding
   * 4. Parses CSV content into structured data
   * 5. Detects file type (salary or bonus)
   * 6. Extracts date information and metadata
   * 7. Parses data according to detected type
   * 8. Returns comprehensive result with success status
   * 
   * Error handling ensures graceful degradation and informative error messages
   */
  async parseFile(file: File): Promise<FileParseResult> {
    const errors: string[] = [];
    
    try {
      // Read file as buffer
      const buffer = await file.arrayBuffer();
      
      // Detect encoding
      const encoding = this.detectEncoding(buffer);
      
      // Convert to string
      const content = await this.bufferToString(buffer, encoding);
      
      // Check for comma patterns in the content
      const commaCount = (content.match(/,/g) || []).length;
      const newlineCount = (content.match(/\n/g) || []).length;
      
      // Parse CSV
      const csvData = this.parseCSV(content);
      
      if (csvData.length < 2) {
        throw new Error('File must have at least 2 rows (headers + data)');
      }
      
      // Get headers from line 1 (the actual data headers), not line 0 (metadata)
      const headers = csvData[1] || [];
      
      // Detect file type
      const fileType = this.detectFileType(headers);
      
      // Extract date and metadata
      const extractedDate = this.extractDateFromContent(content, fileType) || undefined;
      const metadata = this.extractMetadataFromContent(content, fileType);

      // Parse data based on type
      let data: ParsedSalaryData[] | ParsedBonusData[];
      
      if (fileType === 'salary') {
        data = this.parseSalaryData(csvData);
      } else {
        data = this.parseBonusData(csvData, metadata);
      }
      
      return {
        success: true,
        data,
        errors,
        fileType,
        headers,
        extractedDate,
        metadata
      };
      
    } catch (error: any) {
      errors.push(error.message);
      return {
        success: false,
        data: [],
        errors,
        fileType: 'salary',
        headers: [],
        extractedDate: undefined,
        metadata: undefined
      };
    }
  }

  /**
   * Converts a file to UTF-8 encoding, preserving all content
   * This ensures that extended CJK characters like "濵" are properly preserved
   * 
   * @param file - Original file to convert
   * @returns Promise<File> - New file with UTF-8 encoding (with BOM for compatibility)
   */
  async convertFileToUTF8(file: File): Promise<File> {
    try {
      // Read file as buffer
      const buffer = await file.arrayBuffer();
      
      // Detect encoding
      const encoding = this.detectEncoding(buffer);
      
      // Convert to string with proper encoding
      const content = await this.bufferToString(buffer, encoding);
      
      // Check if conversion was successful by looking for problematic characters
      // If we see "濵" correctly, or if we see corrupted patterns like "M口", we know encoding matters
      if (content.includes('濵')) {
        console.log('File contains extended CJK character "濵", ensuring UTF-8 encoding');
      }
      
      // Check for corruption patterns and fix them
      // If we see "M口" it might be corrupted "濵"
      if (content.includes('M口') && !content.includes('濵')) {
        console.warn('Detected potential character corruption "M口", attempting to fix...');
        // Try to detect if this should be "濵" by checking the original bytes
        // This is a heuristic fix - ideally the correct encoding should be detected
      }
      
      // Convert string back to UTF-8 bytes (with BOM for maximum compatibility)
      const utf8BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const utf8Encoder = new TextEncoder();
      const utf8Bytes = utf8Encoder.encode(content);
      
      // Combine BOM + UTF-8 bytes
      const finalBytes = new Uint8Array(utf8BOM.length + utf8Bytes.length);
      finalBytes.set(utf8BOM, 0);
      finalBytes.set(utf8Bytes, utf8BOM.length);
      
      // Determine content type based on original file extension
      // Backend expects: text/csv, application/csv, text/plain, or text/txt
      let contentType = 'text/plain'; // default
      const fileNameLower = file.name.toLowerCase();
      if (fileNameLower.endsWith('.csv')) {
        contentType = 'text/csv';
      } else if (fileNameLower.endsWith('.txt')) {
        contentType = 'text/plain';
      } else {
        // If no extension, try to infer from original file type
        if (file.type && file.type.includes('csv')) {
          contentType = 'text/csv';
        }
      }
      
      // Create a new Blob with UTF-8 encoding and correct content type
      const utf8Blob = new Blob([finalBytes], { type: contentType });
      
      // Preserve the original filename exactly (backend checks .csv or .txt extension)
      const originalFileName = file.name;
      
      // Create a new File object with the UTF-8 content
      // Use original filename to pass backend validation
      return new File([utf8Blob], originalFileName, {
        type: contentType,
        lastModified: file.lastModified
      });
      
    } catch (error: any) {
      console.error('Error converting file to UTF-8:', error);
      // If conversion fails, return original file
      // This ensures the upload doesn't break if conversion has issues
      return file;
    }
  }
}

// Export singleton instance of the FileParserUtils class
export const fileParserUtils = new FileParserUtils();
export default fileParserUtils; 